import { useState, useMemo, useEffect } from "react";
import { 
  Button, 
  Spinner, 
  Alert, 
  Card, 
  Table, 
  Row, 
  Col, 
  Form,
  Badge,
  Container
} from "react-bootstrap";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { FindUsersResponse, User, Event, PaginatedResponse, EventUsers, Memberships } from "../../types/Types";
import { FiDownload, FiFilter, FiRefreshCw, FiEye, FiBarChart2 } from "react-icons/fi";

const PAGE_SIZE = 200;

function escapeCsvValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return "";
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const CSV_HEADERS: { header: string; key: string }[] = [
  { header: "Id", key: "id" },
  { header: "ObjectId", key: "objectId" },
  { header: "FirstName", key: "firstName" },
  { header: "LastName", key: "lastName" },
  { header: "Email", key: "email" },
  { header: "PhoneNumber", key: "phoneNumber" },
  { header: "City", key: "city" },
  { header: "Country", key: "country" },
  { header: "BusinessName", key: "businessName" },
  { header: "BusinessSectorIndustry", key: "businessSectorIndustry" },
  { header: "BusinessCountryOperation", key: "businessCountryOperation" },
  { header: "BusinessTurnover", key: "businessTurnover" },
  { header: "MembershipName", key: "membershipName" },
  { header: "ReferralCode", key: "referralCode" },
  { header: "CustomerId", key: "customerId" },
  { header: "SubscriptionId", key: "subscriptionId" },
  { header: "StripeStatus", key: "stripeStatus" },
  { header: "HasPaid", key: "hasPaid" },
  { header: "CreatedDate", key: "createdDate" },
  { header: "LastLoggedIn", key: "lastLoggedIn" },
];

function getValue(user: User, key: string): string | number | undefined | null {
  const value = (user as unknown as Record<string, unknown>)[key];
  return value as string | number | undefined | null;
}

function buildUsersCsv(users: User[]): string {
  if (users.length === 0) return "";

  const rows: string[] = [];
  const separator = "=".repeat(80);

  // Header Section
  rows.push(separator);
  rows.push("USERS EXPORT REPORT");
  rows.push(separator);
  rows.push(`Report Generated:,${new Date().toLocaleString()}`);
  rows.push(`Total Users:,${users.length}`);
  rows.push("");
  rows.push("");

  // User Data Section
  rows.push(separator);
  rows.push("USER DATA");
  rows.push(separator);
  
  const headerRow = CSV_HEADERS.map((h) => h.header).join(",");
  rows.push(headerRow);
  
  users.forEach((u) => {
    const row = CSV_HEADERS.map(({ key }) => escapeCsvValue(getValue(u, key)))
      .join(",");
    rows.push(row);
  });

  // Footer
  rows.push("");
  rows.push(separator);
  rows.push(`End of Report - ${users.length} users exported`);
  rows.push(separator);

  return rows.join("\r\n");
}

async function fetchAllUsers(): Promise<User[]> {
  const all: User[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const url = getAPIURL(
      `/User/All?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`
    );
    const res = await axiosInstance.get<FindUsersResponse>(url);
    const data = res.data?.users?.data ?? [];
    all.push(...data);
    totalPages = res.data?.users?.totalPages ?? 1;
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return all;
}

async function fetchAllEvents(): Promise<Event[]> {
  const all: Event[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  do {
    const url = getAPIURL(
      `/Event/All?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`
    );
    const res = await axiosInstance.get<PaginatedResponse<Event>>(url);
    const data = res.data?.data ?? [];
    all.push(...data);
    totalPages = res.data?.totalPages ?? 1;
    pageNumber += 1;
  } while (pageNumber <= totalPages);

  return all;
}

async function fetchEventUsers(eventId: number): Promise<EventUsers> {
  try {
    const url = getAPIURL(`/Event/event/${eventId}/users`);
    const res = await axiosInstance.get<EventUsers>(url);
    return res.data;
  } catch (error) {
    console.error(`Error fetching users for event ${eventId}:`, error);
    return { eventId, users: [] };
  }
}

async function fetchAllMemberships(): Promise<Memberships[]> {
  try {
    const url = getAPIURL("/Membership/All");
    const res = await axiosInstance.get<Memberships[]>(url);
    return res.data || [];
  } catch (error) {
    console.error("Error fetching memberships:", error);
    return [];
  }
}

interface StripeProductSummary {
  id: string;
  name: string;
  description?: string | null;
  active: boolean;
  defaultPriceId?: string | null;
}

async function fetchStripeProducts(): Promise<StripeProductSummary[]> {
  try {
    const url = getAPIURL("/ProductIncome/products");
    const res = await axiosInstance.get<{ products: StripeProductSummary[] }>(url);
    return res.data?.products || [];
  } catch (error) {
    console.error("Error fetching Stripe products:", error);
    return [];
  }
}

interface MembershipStats {
  membershipType: string;
  payingMembers: number;
  discountCodeMembers: number;
}

interface EventIncome {
  eventId: number;
  eventName: string;
  eventDate: string;
  income: number;
  attendeeCount: number;
}

interface SubscriptionIncome {
  totalSubscriptionIncome: number;
  monthlySubscriptionIncome: number;
  annualSubscriptionIncome: number;
  monthlySubscribers: number;
  annualSubscribers: number;
  totalDiscountAmount: number;
  discountedSubscriptionIncome: number;
}

interface ReportData {
  totalPayingMembers: number;
  totalDiscountCodeMembers: number;
  membershipStats: MembershipStats[];
  eventIncomes: EventIncome[];
  subscriptionIncome: SubscriptionIncome;
  totalEventIncome: number;
  totalIncome: number;
  filteredUsers: User[];
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
}

/**
 * Check if a user has a discount code applied
 * Matches SQL query: DiscountCode IS NOT NULL AND LTRIM(RTRIM(DiscountCode)) <> '' AND IsDeleted = 0
 * Note: IsDeleted check is handled separately in generateReport function
 */
function hasDiscountCode(user: User): boolean {
  const obj = user as any;
  
  // Check DiscountCode field (tries multiple casing variations for API compatibility)
  let discountCode = obj.DiscountCode;
  if (discountCode === null || discountCode === undefined) {
    discountCode = obj.discountCode;
  }
  if (discountCode === null || discountCode === undefined) {
    discountCode = obj.discount_code;
  }
  
  // SQL: DiscountCode IS NOT NULL
  if (discountCode === null || discountCode === undefined) {
    return false;
  }
  
  // SQL: LTRIM(RTRIM(DiscountCode)) <> '' (trim whitespace and check not empty)
  const trimmedCode = String(discountCode).trim();
  return trimmedCode !== '';
}

/**
 * Get discount code from user (handles multiple casing variations)
 */
function getDiscountCode(user: User): string | null {
  const obj = user as any;
  let discountCode = obj.DiscountCode;
  if (discountCode === null || discountCode === undefined) {
    discountCode = obj.discountCode;
  }
  if (discountCode === null || discountCode === undefined) {
    discountCode = obj.discount_code;
  }
  if (discountCode === null || discountCode === undefined) {
    return null;
  }
  const trimmedCode = String(discountCode).trim();
  return trimmedCode !== '' ? trimmedCode : null;
}

/**
 * Calculate discounted price for a membership based on discount code
 * Matches the logic from MemberShipStep.tsx
 */
function calculateDiscountedPrice(
  monthlyFee: number,
  annualFee: number,
  isAnnual: boolean,
  discountCode: string | null
): number {
  const price = isAnnual ? annualFee : monthlyFee;
  
  if (!discountCode) return price;
  
  let discountedPrice = price;
  
  switch (discountCode) {
    case "1COMP": // 1 month free → this month is free
      discountedPrice = isAnnual ? annualFee - monthlyFee : 0;
      break;
    
    case "COMP3": // 3 months free → only affects first 3 invoices
      discountedPrice = isAnnual ? annualFee - monthlyFee * 3 : 0;
      break;
    
    case "WAIVE6": // 6 months free
      discountedPrice = isAnnual ? annualFee - monthlyFee * 6 : 0;
      break;
    
    case "DISCOUNT12": // 12 months free → first year free
      discountedPrice = isAnnual ? 0 : 0;
      break;
    
    case "24OFF": // 24 months free → basically full waiver
      discountedPrice = 0;
      break;
    
    case "53OFF": // 50% off for 3 months
      discountedPrice = isAnnual
        ? annualFee - monthlyFee * 0.5 * 3
        : monthlyFee * 0.5; // 50% off this month
      break;
    
    case "56OFF": // 50% off for 6 months
      discountedPrice = isAnnual
        ? annualFee - monthlyFee * 0.5 * 6
        : monthlyFee * 0.5;
      break;
    
    case "512OFF": // 50% off for 12 months
      discountedPrice = isAnnual ? annualFee / 2 : monthlyFee * 0.5;
      break;
    
    default:
      discountedPrice = price;
  }
  
  return Math.max(discountedPrice, 0);
}

/**
 * Calculate event income based on pricing rules:
 * - If early bird date hasn't passed: uses discounted price (price - earlyBirdPrice)
 * - Otherwise: uses regular price
 * Note: This is an estimate. Actual income may vary based on:
 * - Individual membership discounts (Business/Executive members)
 * - Per-user payment variations
 * - Refunds or cancellations
 */
function calculateEventIncome(event: Event, attendeeCount: number): number {
  if (attendeeCount === 0) return 0;
  
  // Check if early bird pricing is still active
  const isEarlyBirdActive = event.earlyBirdDateEnd && 
    new Date(event.earlyBirdDateEnd) > new Date();
  
  // Calculate price per attendee
  let pricePerAttendee = event.price;
  
  if (isEarlyBirdActive && event.earlyBirdPrice > 0) {
    // Early bird discount: attendees pay (price - earlyBirdPrice)
    pricePerAttendee = event.price - event.earlyBirdPrice;
  }
  
  // Total income = price per attendee × number of attendees
  return pricePerAttendee * attendeeCount;
}

function filterUsersByDate(users: User[], dateFrom: string, dateTo: string): User[] {
  if (!dateFrom && !dateTo) return users;

  return users.filter((user) => {
    const createdDate = (user as any).createdDate;
    if (!createdDate) return false;

    const userDate = new Date(createdDate);
    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(dateTo + "T23:59:59") : null;

    if (fromDate && userDate < fromDate) return false;
    if (toDate && userDate > toDate) return false;
    return true;
  });
}

// Product Income report from backend (Stripe data saved in DB)
interface ProductIncomeDto {
  productId: string;
  productName?: string;
  localProductName?: string;
  totalIncome: number;
  currency: string;
  totalTransactions: number;
  couponUsage: Array<{ couponId: string; couponNameOrCode?: string; useCount: number; totalAmountWithThisCoupon: number }>;
  transactionsWithoutCoupon: number;
  incomeWithoutCoupon: number;
}

interface ProductIncomeReportDto {
  products: ProductIncomeDto[];
  grandTotalIncome: number;
  currency: string;
}

interface SavedProductIncomeResponse {
  generatedAtUtc: string;
  fromDate?: string;
  toDate?: string;
  report: ProductIncomeReportDto;
}

/** Build from/to in UTC for API. When only one date set, use full month so DB filter returns that month's report. */
function buildReportDateRange(dateFrom?: string, dateTo?: string): { from: string; to: string } | null {
  if (!dateFrom && !dateTo) return null;
  const utcStart = (d: string) => new Date(d + "T00:00:00.000Z");
  const utcEnd = (d: string) => new Date(d + "T23:59:59.999Z");
  const lastDayUtc = (y: number, m: number) => new Date(Date.UTC(y, m, 0, 23, 59, 59, 999));
  const firstDayUtc = (y: number, m: number) => new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0));

  if (dateFrom && dateTo) {
    return { from: utcStart(dateFrom).toISOString(), to: utcEnd(dateTo).toISOString() };
  }
  if (dateFrom) {
    const from = utcStart(dateFrom);
    const to = lastDayUtc(from.getUTCFullYear(), from.getUTCMonth() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  if (dateTo) {
    const to = utcEnd(dateTo);
    const from = firstDayUtc(to.getUTCFullYear(), to.getUTCMonth() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  return null;
}

async function generateReport(dateFrom?: string, dateTo?: string): Promise<ReportData> {
  const range = buildReportDateRange(dateFrom || undefined, dateTo || undefined);

  const buildQueryParams = () => {
    if (!range) return "";
    return `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  };

  const [memberships, events, stripeProducts] = await Promise.all([
    fetchAllMemberships(),
    fetchAllEvents(),
    fetchStripeProducts(),
  ]);

  const membershipProductIds = new Set<string>();
  const annualProductIds = new Set<string>();
  memberships.forEach((m) => {
    if (m.productId) membershipProductIds.add(m.productId);
    const annualId = (m as { productIdAnnual?: string }).productIdAnnual;
    if (annualId) {
      membershipProductIds.add(annualId);
      annualProductIds.add(annualId);
    }
  });

  // Build Stripe product lookup and infer additional membership products directly from Stripe
  const stripeProductMap = new Map<string, StripeProductSummary>();
  stripeProducts.forEach((p) => {
    stripeProductMap.set(p.id, p);
    const nameLower = (p.name || "").toLowerCase();
    const isMembership = nameLower.includes("membership");
    const isEvent = nameLower.includes("event");
    if (isMembership && !isEvent) {
      membershipProductIds.add(p.id);
      const isAnnual = nameLower.includes("yearly") || nameLower.includes("annual");
      if (isAnnual) {
        annualProductIds.add(p.id);
      }
    }
  });

  const eventByProductId = new Map<string, { id: number; name: string; eventDate: string }>();
  events.forEach((e) => {
    if (e.productId) {
      eventByProductId.set(e.productId, { id: e.id, name: e.name, eventDate: e.eventDate || "" });
    }
  });
  const eventProductIds = new Set(eventByProductId.keys());

  // Generate & Preview = get from DB only (ProductIncomeReports table). No Stripe sync.
  const res = await axiosInstance.get<SavedProductIncomeResponse>(
    getAPIURL(`/ProductIncome/report/saved${buildQueryParams()}`)
  );
  const report = res.data.report;
  const products = (report.products || []).map((p) => {
    const stripe = stripeProductMap.get(p.productId);
    if (!stripe || p.productName) {
      return p;
    }
    return {
      ...p,
      productName: stripe.name,
    };
  });
  const membershipProducts = products.filter((p) => membershipProductIds.has(p.productId));
  const eventProducts = products.filter((p) => eventProductIds.has(p.productId));

  let monthlySubscriptionIncome = 0;
  let annualSubscriptionIncome = 0;
  let monthlySubscribers = 0;
  let annualSubscribers = 0;
  let totalPayingMembers = 0;
  let totalDiscountCodeMembers = 0;
  const membershipStatsMap = new Map<string, { paying: number; discount: number }>();

  membershipProducts.forEach((p) => {
    const displayName = p.localProductName || p.productName || p.productId;
    const current = membershipStatsMap.get(displayName) || { paying: 0, discount: 0 };
    current.paying += p.totalTransactions;
    current.discount += p.totalTransactions - p.transactionsWithoutCoupon;
    membershipStatsMap.set(displayName, current);

    totalPayingMembers += p.totalTransactions;
    totalDiscountCodeMembers += p.totalTransactions - p.transactionsWithoutCoupon;

    const isAnnual = annualProductIds.has(p.productId);
    if (isAnnual) {
      annualSubscriptionIncome += p.totalIncome;
      annualSubscribers += p.totalTransactions;
    } else {
      monthlySubscriptionIncome += p.totalIncome;
      monthlySubscribers += p.totalTransactions;
    }
  });

  const totalSubscriptionIncome = monthlySubscriptionIncome + annualSubscriptionIncome;
  const membershipStats: MembershipStats[] = Array.from(membershipStatsMap.entries()).map(
    ([membershipType, stats]) => ({
      membershipType,
      payingMembers: stats.paying,
      discountCodeMembers: stats.discount,
    })
  );

  const eventIncomes: EventIncome[] = eventProducts.map((p) => {
    const eventInfo = eventByProductId.get(p.productId);
    return {
      eventId: eventInfo?.id ?? 0,
      eventName: eventInfo?.name ?? p.localProductName ?? p.productName ?? p.productId,
      eventDate: eventInfo?.eventDate ?? new Date().toISOString(),
      income: p.totalIncome,
      attendeeCount: p.totalTransactions,
    };
  });
  const totalEventIncome = eventIncomes.reduce((sum, e) => sum + e.income, 0);
  const totalIncome = report.grandTotalIncome ?? totalSubscriptionIncome + totalEventIncome;

  const allUsers = await fetchAllUsers();
  const filteredUsers = filterUsersByDate(allUsers, dateFrom || "", dateTo || "");
  const activeUsers = filteredUsers.filter((u) => {
    const isDeleted = (u as any).IsDeleted;
    return isDeleted === undefined || isDeleted === null || isDeleted === 0 || isDeleted === false;
  });

  return {
    totalPayingMembers,
    totalDiscountCodeMembers,
    membershipStats,
    eventIncomes,
    subscriptionIncome: {
      totalSubscriptionIncome,
      monthlySubscriptionIncome,
      annualSubscriptionIncome,
      monthlySubscribers,
      annualSubscribers,
      totalDiscountAmount: 0,
      discountedSubscriptionIncome: totalSubscriptionIncome,
    },
    totalEventIncome,
    totalIncome,
    filteredUsers: activeUsers,
  };
}

function buildReportCsv(reportData: ReportData, filters: FilterState): string {
  const rows: string[] = [];
  const separator = "=".repeat(80);

  // Report Header Section
  rows.push(separator);
  rows.push("RECONCILIATION REPORT");
  rows.push(separator);
  rows.push(`Report Generated:,${new Date().toLocaleString()}`);
  rows.push(`Report Period:,${filters.dateFrom || "All Time"} to ${filters.dateTo || "All Time"}`);
  rows.push("");
  rows.push("");

  // Filter Information Section
  rows.push(separator);
  rows.push("FILTERS APPLIED");
  rows.push(separator);
  rows.push("Filter Type,Value");
  rows.push(`Date From,${filters.dateFrom || "All Time"}`);
  rows.push(`Date To,${filters.dateTo || "All Time"}`);
  rows.push(`Total Users Filtered,${reportData.filteredUsers.length}`);
  rows.push("");
  rows.push("");

  // Summary Section
  rows.push(separator);
  rows.push("SUMMARY STATISTICS");
  rows.push(separator);
  rows.push("Metric,Value");
  rows.push(`Total Paying Members,${reportData.totalPayingMembers}`);
  rows.push(`Total Discount Code Members,${reportData.totalDiscountCodeMembers}`);
  rows.push(`Total Events,${reportData.eventIncomes.length}`);
  rows.push("");
  
  // Income Breakdown
  rows.push(separator);
  rows.push("INCOME BREAKDOWN");
  rows.push(separator);
  rows.push("Income Type,Amount (GBP),Details");
  rows.push(`Subscription Income (Monthly),£${reportData.subscriptionIncome.monthlySubscriptionIncome.toFixed(2)},${reportData.subscriptionIncome.monthlySubscribers} subscribers`);
  rows.push(`Subscription Income (Annual),£${reportData.subscriptionIncome.annualSubscriptionIncome.toFixed(2)},${reportData.subscriptionIncome.annualSubscribers} subscribers`);
  rows.push(`Total Subscription Income (After Discounts),£${reportData.subscriptionIncome.totalSubscriptionIncome.toFixed(2)},`);
  if (reportData.subscriptionIncome.totalDiscountAmount > 0) {
    rows.push(`Total Discounts Applied,£${reportData.subscriptionIncome.totalDiscountAmount.toFixed(2)},`);
  }
  rows.push(`Total Event Income,£${reportData.totalEventIncome.toFixed(2)},${reportData.eventIncomes.length} events`);
  rows.push(`TOTAL INCOME,£${reportData.totalIncome.toFixed(2)},Subscription + Events`);
  rows.push("");
  rows.push("Note: Total Income is an ESTIMATE based on event pricing rules and attendee counts.");
  rows.push("Actual income may vary due to membership discounts and payment variations.");
  rows.push("");
  rows.push("");

  // Membership Statistics Section
  rows.push(separator);
  rows.push("MEMBERSHIP STATISTICS BY TYPE");
  rows.push(separator);
  rows.push("Membership Type,Paying Members,Discount Code Members,Total Members");
  
  let totalPayingAllTypes = 0;
  let totalDiscountAllTypes = 0;
  
  reportData.membershipStats.forEach((stat) => {
    const total = stat.payingMembers + stat.discountCodeMembers;
    totalPayingAllTypes += stat.payingMembers;
    totalDiscountAllTypes += stat.discountCodeMembers;
    rows.push(
      `${escapeCsvValue(stat.membershipType)},${stat.payingMembers},${stat.discountCodeMembers},${total}`
    );
  });
  
  // Add totals row
  rows.push(`TOTAL,${totalPayingAllTypes},${totalDiscountAllTypes},${totalPayingAllTypes + totalDiscountAllTypes}`);
  rows.push("");
  rows.push("");

  // Subscription Income Section
  rows.push(separator);
  rows.push("SUBSCRIPTION INCOME (WITH DISCOUNTS APPLIED)");
  rows.push(separator);
  rows.push("Subscription Type,Subscribers,Income (GBP)");
  rows.push(`Monthly Subscriptions,${reportData.subscriptionIncome.monthlySubscribers},£${reportData.subscriptionIncome.monthlySubscriptionIncome.toFixed(2)}`);
  rows.push(`Annual Subscriptions,${reportData.subscriptionIncome.annualSubscribers},£${reportData.subscriptionIncome.annualSubscriptionIncome.toFixed(2)}`);
  rows.push(`Total Subscription Income (After Discounts),,£${reportData.subscriptionIncome.totalSubscriptionIncome.toFixed(2)}`);
  if (reportData.subscriptionIncome.totalDiscountAmount > 0) {
    rows.push(`Total Discounts Applied,,£${reportData.subscriptionIncome.totalDiscountAmount.toFixed(2)}`);
  }
  rows.push("");

  // Event Income Section
  rows.push(separator);
  rows.push("INCOME PER EVENT");
  rows.push(separator);
  rows.push("Note: Income is calculated based on event pricing rules:");
  rows.push("- Early bird pricing applied if early bird date hasn't passed");
  rows.push("- Base price used if early bird period has ended");
  rows.push("- This is an ESTIMATE and may not reflect actual payments due to:");
  rows.push("  * Individual membership discounts (Business/Executive members)");
  rows.push("  * Per-user payment variations");
  rows.push("  * Refunds or cancellations");
  rows.push("");
  rows.push("Event Name,Event Date,Attendee Count,Estimated Income (GBP)");
  
  let totalAttendees = 0;
  
  reportData.eventIncomes.forEach((event) => {
    totalAttendees += event.attendeeCount;
    const formattedDate = event.eventDate ? new Date(event.eventDate).toLocaleDateString() : "N/A";
    rows.push(
      `${escapeCsvValue(event.eventName)},${formattedDate},${event.attendeeCount},£${event.income.toFixed(2)}`
    );
  });
  
  // Add totals row
  rows.push(`TOTAL EVENT INCOME,,${totalAttendees},£${reportData.totalEventIncome.toFixed(2)}`);
  rows.push("");
  rows.push("");

  // User Details Section
  rows.push(separator);
  rows.push("USER DETAILS");
  rows.push(separator);
  rows.push(`Total Users in Report:,${reportData.filteredUsers.length}`);
  rows.push("");
  
  // Group user fields into logical sections for better readability
  const userHeaders = CSV_HEADERS.map((h) => h.header).join(",");
  rows.push(userHeaders);
  
  reportData.filteredUsers.forEach((user, index) => {
    const row = CSV_HEADERS.map(({ key }) => escapeCsvValue(getValue(user, key))).join(",");
    rows.push(row);
  });

  // Footer
  rows.push("");
  rows.push(separator);
  rows.push(`End of Report - Generated on ${new Date().toLocaleString()}`);
  rows.push(separator);

  return rows.join("\r\n");
}

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // Empty filter = backend returns current month by default; user can apply filter for custom range
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: "",
    dateTo: "",
  });

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  }, [filters]);

  // Load report data automatically on component mount and when filters change
  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      setMessage(null);
      setShowPreview(false);

      try {
        setMessage("Loading report data...");
        const data = await generateReport(filters.dateFrom, filters.dateTo);
        setReportData(data);
        setShowPreview(true);
        setMessage(null); // Clear loading message once data is loaded
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { data?: unknown } }).response?.data
            : err;
        setError(
          typeof msg === "string" ? msg : "Failed to load report data. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [filters.dateFrom, filters.dateTo]); // Load on mount and when filters change

  const handleSyncAndDownloadReport = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      setMessage("Syncing Stripe data for all users...");
      await axiosInstance.post(getAPIURL("/User/sync-stripe-all"));
      setMessage("Fetching all users for report...");
      const users = await fetchAllUsers();
      const csv = buildUsersCsv(users);
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`Report downloaded. Total users: ${users.length}`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: unknown } }).response?.data
          : err;
      setError(
        typeof msg === "string" ? msg : "Sync or download failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    setShowPreview(false);

    try {
      setMessage("Generating reconciliation report...");
      const data = await generateReport(filters.dateFrom, filters.dateTo);
      setReportData(data);
      setShowPreview(true);
      setMessage("Report generated successfully!");
    } catch (err: unknown) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: unknown; status?: number } }).response?.data
          : null;
      const msg: string | null =
        data && typeof data === "object" && data !== null && "message" in data
          ? (data as { message?: string }).message ?? null
          : typeof data === "string"
            ? data
            : "Failed to generate report. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!reportData) return;

    const csv = buildReportCsv(reportData, filters);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleResetFilters = () => {
    setFilters({ dateFrom: "", dateTo: "" });
    setReportData(null);
    setShowPreview(false);
  };

  return (
    <Container fluid className="mt-3">
      {/* Header */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-project-gradient">
          <h4 className="mb-0">
            <FiRefreshCw className="me-2" />
            Financial / User report
          </h4>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            Generate comprehensive reconciliation reports with membership statistics, 
            income analysis, and user details. Filter by creation date to analyze 
            specific time periods.
          </p>

         

          {/* 1. Membership Statistics by Type */}
          {reportData && (
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Membership Statistics by Type</h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Membership Type</th>
                        <th className="text-center">Paying Members</th>
                        <th className="text-center">Discount Code Members</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.membershipStats.length > 0 ? (
                        reportData.membershipStats.map((stat, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>{stat.membershipType}</strong>
                            </td>
                            <td className="text-center">
                              <Badge bg="primary">{stat.payingMembers}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="warning" text="dark">
                                {stat.discountCodeMembers}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            No membership data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* 2. Income Per Event */}
          {reportData && (
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Income Per Event</h5>
                <small className="text-muted">
                  *Estimated income based on pricing rules (early bird discounts applied where applicable)
                </small>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Event Name</th>
                        <th>Event Date</th>
                        <th className="text-center">Attendees</th>
                        <th className="text-end">Estimated Income</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.eventIncomes.length > 0 ? (
                        reportData.eventIncomes.map((event) => (
                          <tr key={event.eventId}>
                            <td>
                              <strong>{event.eventName}</strong>
                            </td>
                            <td>{new Date(event.eventDate).toLocaleDateString()}</td>
                            <td className="text-center">
                              <Badge bg="secondary">{event.attendeeCount}</Badge>
                            </td>
                            <td className="text-end">
                              <strong className="text-success">
                                £{event.income.toFixed(2)}
                              </strong>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            No event data available
                          </td>
                        </tr>
                      )}
                      {reportData.eventIncomes.length > 0 && (
                        <tr className="table-info">
                          <td colSpan={3}>
                            <strong>Total (All Events)</strong>
                          </td>
                          <td className="text-end">
                            <strong className="text-success">
                              £{reportData.totalEventIncome.toFixed(2)}
                            </strong>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* 3. Report Details Summary Card */}
          {reportData && (
            <Card className="mb-4">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <FiBarChart2 className="me-2" />
                  Report Details
                  {activeFiltersCount > 0 && (
                    <Badge bg="info" className="ms-2">Filtered</Badge>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                {/* Line 1: Total members, discount members, membership types */}
                <Row className="g-3">
                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Total Paying Members
                        </div>
                        <div className="fs-3 fw-bold text-primary">
                          {reportData.totalPayingMembers}
                        </div>
                      </div>
                      <div className="text-muted small mt-2">
                        {reportData.filteredUsers.length} total users filtered
                      </div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Discount Code Members
                        </div>
                        <div className="fs-3 fw-bold text-warning">
                          {reportData.totalDiscountCodeMembers}
                        </div>
                      </div>
                      <div className="text-muted small mt-2">
                        {activeFiltersCount > 0 ? "Filtered results" : "All time"}
                      </div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Membership Types
                        </div>
                        <div className="fs-3 fw-bold text-secondary">
                          {reportData.membershipStats.length}
                        </div>
                      </div>
                      <div
                        className="text-muted small text-break mt-2"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {reportData.membershipStats.slice(0, 3).map(s => s.membershipType).join(", ")}
                        {reportData.membershipStats.length > 3 && " …"}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Line 2: Subscription income, event income, total income */}
                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Subscription Income
                        </div>
                        <div className="fs-3 fw-bold text-primary">
                          £{reportData.subscriptionIncome.totalSubscriptionIncome.toFixed(2)}
                        </div>
                      </div>
                      <div className="mt-2">
                        {reportData.subscriptionIncome.totalDiscountAmount > 0 && (
                          <div className="text-warning small mb-1">
                            Discounts: £{reportData.subscriptionIncome.totalDiscountAmount.toFixed(2)}
                          </div>
                        )}
                        <div className="text-muted small">
                          Monthly: £{reportData.subscriptionIncome.monthlySubscriptionIncome.toFixed(2)} (
                          {reportData.subscriptionIncome.monthlySubscribers} members)
                        </div>
                        <div className="text-muted small">
                          Annual: £{reportData.subscriptionIncome.annualSubscriptionIncome.toFixed(2)} (
                          {reportData.subscriptionIncome.annualSubscribers} members)
                        </div>
                        <div className="text-muted small mt-1" style={{ fontSize: "0.75rem" }}>
                          *After discount codes applied
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Event Income
                        </div>
                        <div className="fs-3 fw-bold text-info">
                          £{reportData.totalEventIncome.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-muted small mt-2">
                        From {reportData.eventIncomes.length} events
                      </div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="p-3 rounded bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="text-muted small text-uppercase mb-2">
                          Total Income
                        </div>
                        <div className="fs-3 fw-bold text-success">
                          £{reportData.totalIncome.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-muted small mt-2">
                        Subscription + Events
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* 4. Filters Section */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FiFilter className="me-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge bg="primary" className="ms-2">{activeFiltersCount}</Badge>
                )}
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Created Date From</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) =>
                        setFilters({ ...filters, dateFrom: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Created Date To</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) =>
                        setFilters({ ...filters, dateTo: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="d-flex gap-2 align-items-center pt-1">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleGenerateReport}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FiEye className="me-2" />
                          Generate & Preview Report
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={handleResetFilters}
                      disabled={loading}
                    >
                      Reset Filters
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Active Filters Preview */}
              {activeFiltersCount > 0 && (
                <div className="mt-3">
                  <small className="text-muted">Active Filters:</small>
                  <div className="mt-1">
                    {filters.dateFrom && (
                      <Badge bg="info" className="me-2">
                        From: {new Date(filters.dateFrom).toLocaleDateString()}
                      </Badge>
                    )}
                    {filters.dateTo && (
                      <Badge bg="info" className="me-2">
                        To: {new Date(filters.dateTo).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
 {/* Fixed Main Header */}
 <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "linear-gradient(90deg,#f8fafc,#e3e8ef)", // match bg-project-gradient as fallback
            }}
            className="mb-4 shadow-sm border-0"
          >
            <div className="px-4 py-3 d-flex align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FiRefreshCw className="me-2" />
                Financial / User report
              </h4>
            </div>
          </div>
          {/* 5. Export & Download Actions */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FiDownload className="me-2" />
                Reconciliation Report
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Use the options below to sync payment data with Stripe and download reports in CSV format for analysis or record-keeping.
              </p>
              <Row>
                <Col md={6}>
                  <div className="border rounded p-3 mb-2 mb-md-0">
                    <h6 className="mb-2">Sync Stripe & Download Users CSV</h6>
                    <p className="text-muted small mb-2 mb-0">
                      Syncs subscription and payment status for all users from Stripe, then downloads a complete user list with profile data, membership details, Stripe status, and payment info. Use this for user management or external audits.
                    </p>
                    <Button
                      variant="outline-primary"
                      onClick={handleSyncAndDownloadReport}
                      disabled={loading}
                    >
                      <FiDownload className="me-2" />
                      Sync Stripe & Download Users CSV
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3">
                    <h6 className="mb-2">Download Reconciliation Report</h6>
                    <p className="text-muted small mb-2 mb-0">
                      Downloads the full reconciliation report including membership statistics, subscription income, event income breakdown, and user details. Use filters above to generate the report first.
                    </p>
                    <Button
                      variant="success"
                      onClick={handleDownloadReport}
                      disabled={loading || !reportData}
                    >
                      <FiDownload className="me-2" />
                      Download Reconciliation Report
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Messages */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          {error}
        </Alert>
      )}
      {message && !error && (
        <Alert variant="success" onClose={() => setMessage(null)} dismissible className="mb-4">
          {message}
        </Alert>
      )}

      {/* Report Preview */}
      {showPreview && reportData && (
        <div className="mt-4">
          {/* User Count Summary */}
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Filtered Users Summary</h5>
            </Card.Header>
            <Card.Body>
              <p className="mb-0">
                <strong>Total Users:</strong> {reportData.filteredUsers.length}
                {activeFiltersCount > 0 && (
                  <span className="text-muted ms-2">
                    (Filtered by creation date)
                  </span>
                )}
              </p>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default Reports;
